import api from '../api/axios';

const BASE_URL = 'api/ai-agent';

/**
 * Invia un messaggio e/o file all'AI agent utilizzando FormData.
 * @param {FormData} formData - L'oggetto FormData che contiene il testo del messaggio, i file e il chat_id.
 * @returns {Promise} - La promessa con la risposta dell'AI
 */
export const sendChatMessage = (formData, onUploadProgress) => {
  // Non impostare Content-Type: axios lo aggiunge automaticamente con il boundary corretto per FormData
  return api.post(`${BASE_URL}/chat/`, formData, {
    onUploadProgress,
  });
};

export const uploadChatAttachment = (formData, onUploadProgress) => {
  return api.post(`${BASE_URL}/chat-attachments/`, formData, {
    onUploadProgress,
  });
};

/**
 * Recupera la lista delle chat.
 * Se from_session_id è fornito, recupera le chat più vecchie di quella specifica (paginazione).
 * Altrimenti, recupera la lista iniziale delle chat più recenti.
 * 
 * @param {string | null | undefined} from_session_id - L'ID della chat da cui iniziare a cercare le più vecchie.
 * @returns {Promise} - La promessa della chiamata API.
 */
export const getChatHistoryNames = (from_session_id) => {
  // Il percorso base dell'endpoint rimane lo stesso.
  // NOTA: Assicurati che questo percorso corrisponda a quello definito nel tuo urls.py di Django.
  // Se la view si chiama 'get_working_memories', il percorso potrebbe essere '/memory/working_memories/'
  let url = `${BASE_URL}/memory/active_chats/`;

  // Aggiungiamo il parametro di query SOLO se from_session_id è valido.
  if (from_session_id) {
    url += `?from_session_id=${encodeURIComponent(from_session_id)}`;
  }

  // Ora l'URL sarà corretto in entrambi i casi:
  // 1. Senza ID: http://.../api/memory/active_chats/
  // 2. Con ID:    http://.../api/memory/active_chats/?from_session_id=xyz-123

  return api.get(url);
};

export const deleteChatService = (sessionId) => {
  return api.delete(`${BASE_URL}/memory/chat_history/${sessionId}/`);
};

export const getChatHistory = (sessionId) => {
  return api.get(`${BASE_URL}/memory/chat_history/${sessionId}/`);
};

export const getChatTitle = (message, chat_id) => {
  return api.post(`${BASE_URL}/conversations/${chat_id}/generate_title/`, { message });
};

export const editChatTitle = (new_title, chat_id) => {
  return api.patch(`${BASE_URL}/conversations/${chat_id}/`, { title: new_title });
}

/**
 * Recupera la lista degli agenti disponibili.
 * GET /api/ai-agent/agents/
 * @returns {Promise} - La promessa con la lista degli agenti
 */
export const getAgents = () => {
  return api.get(`${BASE_URL}/agents/`);
};

/**
 * Recupera l'agente di default.
 * GET /api/ai-agent/agents/default/
 * @returns {Promise} - La promessa con l'agente di default
 */
export const getDefaultAgent = () => {
  return api.get(`${BASE_URL}/agents/default/`);
};

// ============================================================
// Conversations API (nuovi endpoint REST)
// ============================================================

/**
 * GET /api/ai-agent/conversations/ — lista conversazioni
 */
export const getConversations = () => {
  return api.get(`${BASE_URL}/conversations/`);
};

/**
 * POST /api/ai-agent/conversations/ — crea nuova conversazione
 */
export const createConversation = (title) => {
  return api.post(`${BASE_URL}/conversations/`, { title });
};

/**
 * DELETE /api/ai-agent/conversations/{chat_id}/ — elimina conversazione
 */
export const deleteConversation = (chatId) => {
  return api.delete(`${BASE_URL}/conversations/${chatId}/`);
};

/**
 * GET /api/ai-agent/conversations/{chat_id}/messages/ — messaggi di una conversazione
 */
export const getConversationMessages = (chatId) => {
  return api.get(`${BASE_URL}/conversations/${chatId}/messages/`);
};

/**
 * Esegue un'azione suggerita dall'AI agent
 * @param {number} actionId - L'ID dell'azione da eseguire
 * @param {Object} data - Dati opzionali per l'esecuzione dell'azione
 * @returns {Promise} - La promessa con il risultato dell'azione
 */
export const executeAction = (actionId, data = {}) => {
  return api.post(`${BASE_URL}/action/${actionId}/`, data);
}; 
